/**
 * Shared domain types for the data export feature.
 *
 * The backend exposes two endpoints per entity:
 *   GET /<entity>/export/preview  → ExportPreview (count + columns + filter chips)
 *   GET /<entity>/export           → binary file with the chosen format/columns
 *
 * These types model both contracts so the export modal works uniformly across
 * investors, beneficiaries, investments and future entities (e.g. classifications).
 */

export type ExportFormat = "csv" | "xlsx" | "json";

export interface ExportColumn {
  key: string;
  label: string;
  default_selected: boolean;
}

export interface ExportFilterChip {
  key: string;
  label: string;
  value: string;
  value_label: string;
}

export interface ExportPreview {
  count: number;
  columns: ExportColumn[];
  filters: ExportFilterChip[];
  available_formats: ExportFormat[];
}

export interface ExportDownloadOptions {
  format: ExportFormat;
  columns: string[];
  sort_by?: string;
  sort_direction?: "asc" | "desc" | null;
  /** When set, overrides the filename suggested by the backend's
   * Content-Disposition header. Used by preset flows that need a
   * specific filename (e.g. "Solicitar correcciones al promotor"
   * wants ``solicitud_correcciones_<promoter>_<date>.xlsx``, not the
   * generic ``classifications_<timestamp>.xlsx``). */
  filenameOverride?: string;
}

export const DEFAULT_EXPORT_FORMAT: ExportFormat = "xlsx";

/**
 * Above this row count the modal prompts a "large dataset" confirmation step.
 * Kept conservative because XLSX serialization in openpyxl scales linearly.
 */
export const LARGE_EXPORT_THRESHOLD = 50_000;
